/**
 * Storybook (and dev mock auth): `aws-amplify/auth` exports live on a namespace
 * object whose properties are not assignable — patching `Auth.xxx = …` throws at
 * runtime. Overrides are applied through a Proxy instead.
 *
 * This module is not used in production builds: `~/amplify` wires plain Auth
 * unless `IS_STORYBOOK` or mock auth is active.
 */
const mfaStoryAuthOverrides: Record<string, unknown> = {};

function wrapAuthWithStoryOverrides<T extends object>(base: T): T {
  return new Proxy(base, {
    get(target, prop, receiver) {
      if (
        typeof prop === 'string' &&
        Object.prototype.hasOwnProperty.call(mfaStoryAuthOverrides, prop)
      ) {
        return mfaStoryAuthOverrides[prop];
      }
      const value = Reflect.get(target, prop, receiver);
      if (typeof value === 'function') {
        return (value as (...args: Array<unknown>) => unknown).bind(target);
      }
      return value;
    },
  }) as T;
}

/**
 * Temporarily replace selected Auth methods (for Storybook). Returns a cleanup
 * function that removes only the keys applied in this call.
 */
export const applyMfaStoryAuthPatches = (
  patches: Record<string, unknown>,
): (() => void) => {
  const appliedKeys: Array<string> = [];
  for (const [key, value] of Object.entries(patches)) {
    if (value !== undefined) {
      mfaStoryAuthOverrides[key] = value;
      appliedKeys.push(key);
    }
  }
  return () => {
    for (const key of appliedKeys) {
      delete mfaStoryAuthOverrides[key];
    }
  };
};

export { wrapAuthWithStoryOverrides };
